import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtDate, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/UI";
import RowDeleteButton from "@/components/app/RowDeleteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import SearchablePicker, { leadMatch, renderLeadRow } from "@/components/app/SearchablePicker";
import EmployeePicker from "@/components/app/EmployeePicker";
import { PRIORITY_OPTIONS, PriorityBadge } from "@/components/app/priority";
import Pagination from "@/components/app/Pagination";
import { Plus, Search } from "lucide-react";
import { toast } from "sonner";

export default function Projects() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [profitMap, setProfitMap] = useState({});
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const [pmFilter, setPmFilter] = useState("all");
  const [capFilter, setCapFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [ownershipFilter, setOwnershipFilter] = useState("all");
  const [subsidyFilter, setSubsidyFilter] = useState("all");
  const [loadEnhFilter, setLoadEnhFilter] = useState("all"); // all | yes | no
  const [stageFilter, setStageFilter] = useState("all"); // all | stage label → only projects where that stage is pending
  const [tab, setTab] = useState("active"); // active | completed
  const [completeTarget, setCompleteTarget] = useState(null); // project pending closure confirmation
  const [completeNotes, setCompleteNotes] = useState("");
  const [closureType, setClosureType] = useState("completed"); // "completed" | "cancelled"
  const [sortBy, setSortBy] = useState("default"); // default | priority | newest | oldest | contract-desc | contract-asc | capacity-desc
  // Server-side pagination: /projects?paged=1&slim=1 returns {items,total}
  // with all filters + sort applied server-side.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [filterOpts, setFilterOpts] = useState({ pms: [], pending_stage_labels: [], counts: { active: 0, completed: 0 } });
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q), 400);
    return () => clearTimeout(t);
  }, [q]);
  const buildParams = (overrides = {}) => {
    const params = new URLSearchParams({
      paged: "1", slim: "1",
      limit: String(pageSize), skip: String((page - 1) * pageSize),
      ...overrides,
    });
    if (debouncedQ.trim()) params.set("q", debouncedQ.trim());
    if (pmFilter !== "all") params.set("pm", pmFilter);
    if (priorityFilter !== "all") params.set("priority", priorityFilter);
    const bucket = { "0-5": [0, 4.9999], "5-10": [5, 9.9999], "10-25": [10, 24.9999], "25-50": [25, 49.9999], "50+": [50, null] }[capFilter];
    if (bucket) { params.set("min_kw", String(bucket[0])); if (bucket[1] != null) params.set("max_kw", String(bucket[1])); }
    if (ownershipFilter !== "all") params.set("ownership", ownershipFilter);
    if (subsidyFilter !== "all") params.set("subsidy", subsidyFilter);
    if (loadEnhFilter !== "all") params.set("load_enh", loadEnhFilter);
    if (stageFilter !== "all") params.set("pending_stage", stageFilter);
    params.set("completed", tab === "completed" ? "completed" : "active");
    const sortMap = {
      priority: ["priority", "asc"], newest: ["created_at", "desc"], oldest: ["created_at", "asc"],
      "contract-desc": ["contract_value_inr", "desc"], "contract-asc": ["contract_value_inr", "asc"],
      "capacity-desc": ["capacity_kw", "desc"], "capacity-asc": ["capacity_kw", "asc"],
    };
    const [sc, sd] = sortMap[sortBy] || ["created_at", "desc"];
    params.set("sort_col", sc); params.set("sort_dir", sd);
    return params;
  };
  const load = () => {
    api.get(`/projects?${buildParams()}`).then((r) => { setProjects(r.data.items || []); setTotal(r.data.total || 0); });
    api.get("/projects/filter-options").then((r) => setFilterOpts(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/projects-profitability").then((r) => setProfitMap(r.data || {})).catch(() => setProfitMap({}));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ },
    [isAdmin, page, pageSize, debouncedQ, pmFilter, capFilter, priorityFilter, ownershipFilter, subsidyFilter, loadEnhFilter, stageFilter, tab, sortBy]);
  // Reset to page 1 whenever filters or page size change so the user never
  // lands on a blank page.
  useEffect(() => { setPage(1); },
    [debouncedQ, pmFilter, capFilter, priorityFilter, ownershipFilter, subsidyFilter, loadEnhFilter, stageFilter, tab, sortBy, pageSize]);
  const editing = editId ? projects.find((p) => p.id === editId) : null;

  // Table scroll plumbing: a slim scrollbar strip on top mirrors the table's
  // horizontal scroll, and the table body scrolls inside a viewport-height
  // container so the sticky header row stays visible on long lists.
  const tableWrapRef = useRef(null);
  const topScrollRef = useRef(null);
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const syncTopFromTable = () => {
    if (topScrollRef.current && tableWrapRef.current) topScrollRef.current.scrollLeft = tableWrapRef.current.scrollLeft;
  };
  const syncTableFromTop = () => {
    if (tableWrapRef.current && topScrollRef.current) tableWrapRef.current.scrollLeft = topScrollRef.current.scrollLeft;
  };

  const pmOptions = filterOpts.pms || [];
  const capacityBuckets = [
    { value: "all", label: "All capacities" },
    { value: "0-5", label: "0–5 kW" },
    { value: "5-10", label: "5–10 kW" },
    { value: "10-25", label: "10–25 kW" },
    { value: "25-50", label: "25–50 kW" },
    { value: "50+", label: "50+ kW" },
  ];

  // List rows come from /projects/list-rows with stage label + progress
  // computed server-side (v2_completion_pct / v2_current_stage_label /
  // pending_stage_labels). Fall back to local stages_v2 math when present.
  const v2Pct = (p) => {
    if (p.v2_completion_pct != null) return p.v2_completion_pct;
    const sv = p.stages_v2 || [];
    if (!sv.length) return p.completion_pct || 0;
    let total = 0, done = 0;
    for (const s of sv) {
      for (const sa of (s.sub_activities || [])) { total += 1; if (sa.done) done += 1; }
    }
    return total ? Math.round((done / total) * 100) : 0;
  };
  const v2Current = (p) => {
    if (p.v2_current_stage_label) return p.v2_current_stage_label;
    const sv = p.stages_v2 || [];
    const ip = sv.find((s) => s.status === "in_progress");
    if (ip) return ip.label;
    const pending = sv.find((s) => s.status === "pending");
    if (pending) return pending.label;
    return sv.length ? sv[sv.length - 1].label : (p.current_stage || "—");
  };

  // Pending-stage filter options come from /projects/filter-options (all
  // projects, not just the current page).
  const stageOptions = filterOpts.pending_stage_labels || [];

  // Keep the top scrollbar strip the same width as the table so it tracks 1:1.
  useEffect(() => {
    const wrap = tableWrapRef.current;
    if (!wrap) return;
    const update = () => setTableScrollWidth(wrap.scrollWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [projects.length, isAdmin]);

  return (
    <div data-testid="projects-page">
      <PageHeader
        eyebrow="Execution"
        title="Projects"
        subtitle="10 milestone tracking from Order Confirmation through Project Closure. Every signed deal lives here."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="new-project-button">
                <Plus className="w-4 h-4 mr-2" /> New Project
              </Button>
            </DialogTrigger>
            {/* Mount only while open so each session gets a fresh form state. */}
            {open && <ProjectDialog onDone={() => { setOpen(false); load(); }} />}
          </Dialog>
        }
      />
      <div className="p-4 sm:p-8">
        {/* Active / Completed tabs */}
        <div className="mb-4 flex flex-wrap gap-1" data-testid="projects-tabs">
          {[
            { k: "active", label: `Active (${filterOpts.counts?.active ?? 0})` },
            { k: "completed", label: `Completed (${filterOpts.counts?.completed ?? 0})` },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 h-9 text-[11px] uppercase tracking-widest font-bold border ${tab === t.k ? "bg-[#0a0a0a] text-white border-[#0a0a0a]" : "bg-white text-[#0a0a0a] border-[#E5E7EB] hover:bg-[#F9FAFB]"}`}
              data-testid={`projects-tab-${t.k}`}
            >{t.label}</button>
          ))}
        </div>
        <div className="mb-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by customer, phone, location, QR, consumer no…"
              className="rounded-none h-10 pl-9"
              data-testid="projects-search-input"
            />
          </div>
          <Select value={capFilter} onValueChange={setCapFilter}>
            <SelectTrigger className="rounded-none h-10 w-full sm:w-40" data-testid="projects-capacity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{capacityBuckets.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={pmFilter} onValueChange={setPmFilter}>
            <SelectTrigger className="rounded-none h-10 w-full sm:w-48" data-testid="projects-pm-filter"><SelectValue placeholder="All Project Managers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Project Managers</SelectItem>
              {pmOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="rounded-none h-10 w-full sm:w-40" data-testid="projects-priority-filter"><SelectValue placeholder="All priorities" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              {PRIORITY_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={ownershipFilter} onValueChange={setOwnershipFilter}>
            <SelectTrigger className="rounded-none h-10 w-full sm:w-44" data-testid="projects-ownership-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All (Ownership)</SelectItem>
              <SelectItem value="yes">Ownership Change</SelectItem>
              <SelectItem value="no">No Ownership Change</SelectItem>
            </SelectContent>
          </Select>
          <Select value={subsidyFilter} onValueChange={setSubsidyFilter}>
            <SelectTrigger className="rounded-none h-10 w-full sm:w-40" data-testid="projects-subsidy-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All (Subsidy)</SelectItem>
              <SelectItem value="with">With Subsidy</SelectItem>
              <SelectItem value="without">Without Subsidy</SelectItem>
              <SelectItem value="unset">Not Set</SelectItem>
            </SelectContent>
          </Select>
          <Select value={loadEnhFilter} onValueChange={setLoadEnhFilter}>
            <SelectTrigger className="rounded-none h-10 w-full sm:w-48" data-testid="projects-load-enh-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All (Load Enhancement)</SelectItem>
              <SelectItem value="yes">Load Enhancement Needed</SelectItem>
              <SelectItem value="no">No Load Enhancement</SelectItem>
            </SelectContent>
          </Select>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="rounded-none h-10 w-full sm:w-56" data-testid="projects-stage-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All Stages</SelectItem>
              {stageOptions.map((s) => <SelectItem key={s} value={s}>Pending: {s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            onClick={async () => {
              try {
                // Fetch ALL filtered ids (unpaged) then hand them to the export endpoint.
                const r = await api.get(`/projects?${buildParams({ limit: "2000", skip: "0" })}`);
                const ids = (r.data.items || []).map((p) => p.id);
                const params = new URLSearchParams({ fmt: "xlsx", ids: ids.join(","), filtered: "1" });
                const a = document.createElement("a");
                a.href = `/api/projects/export?${params.toString()}`;
                a.click();
                toast.success(`Exporting ${ids.length} project${ids.length === 1 ? "" : "s"}…`);
              } catch { toast.error("Export failed"); }
            }}
            variant="outline"
            className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-10"
            data-testid="projects-export-btn"
          >
            Export Excel ({total})
          </Button>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="rounded-none h-10 w-full sm:w-48" data-testid="projects-sort-select"><SelectValue placeholder="Sort by…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Sort: Default</SelectItem>
              <SelectItem value="priority">Priority (Urgent → Low)</SelectItem>
              <SelectItem value="newest">Newest first</SelectItem>
              <SelectItem value="oldest">Oldest first</SelectItem>
              <SelectItem value="contract-desc">Contract Value (High → Low)</SelectItem>
              <SelectItem value="contract-asc">Contract Value (Low → High)</SelectItem>
              <SelectItem value="capacity-desc">Capacity (High → Low)</SelectItem>
              <SelectItem value="capacity-asc">Capacity (Low → High)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div
          ref={topScrollRef}
          onScroll={syncTableFromTop}
          className="overflow-x-auto overflow-y-hidden border border-b-0 border-[#E5E7EB]"
          style={{ height: 14 }}
          data-testid="projects-top-scrollbar"
        >
          <div style={{ width: tableScrollWidth, height: 1 }} />
        </div>
        <div
          ref={tableWrapRef}
          onScroll={syncTopFromTable}
          className="border border-[#E5E7EB] overflow-auto max-h-[calc(100vh-260px)]"
          data-testid="projects-table-wrap"
        >
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] sticky top-0 z-10 shadow-[0_1px_0_#E5E7EB]">
              <tr>
                {["Priority", "Customer", "Panel (kW)", "Inverter (kW)", "Panel Brand", "Inverter Brand", "Project Manager", "Contract", ...(isAdmin ? ["Profit"] : []), "Stage", "Progress", "QR", "", "", ""].map((h, i) => (
                  <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => {
                const prof = profitMap[p.id];
                const margin = prof ? Number(prof.gross_margin || 0) : null;
                const marginPct = prof ? Number(prof.margin_pct || 0) : null;
                const marginColor = margin == null ? "#9CA3AF" : margin >= 0 ? "#10B981" : "#DC2626";
                const ownershipHold = !!p.ownership_change_needed;
                const loadEnh = !!p.load_enhancement_needed;
                // Red bg priority: load enhancement outranks ownership so the
                // stronger operational signal wins when both are set.
                const rowBg = loadEnh ? "bg-[#FEE2E2] hover:bg-[#FECACA]"
                  : ownershipHold ? "bg-[#FEF2F2] hover:bg-[#FEE2E2]"
                  : "";
                return (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/projects/${p.id}`)}
                  className={`border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer ${rowBg}`}
                  data-testid={`project-row-${p.id}`}
                >
                  <td className="px-4 py-3"><PriorityBadge priority={p.priority || "Low"} /></td>
                  <td className="px-4 py-3 w-[220px] max-w-[220px]">
                    <div className="font-semibold flex items-center gap-2 flex-wrap break-words">
                      {p.customer_name}
                      {loadEnh && (
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#DC2626] text-white" data-testid={`project-load-enh-badge-${p.id}`}>
                          Load Enhancement
                        </span>
                      )}
                      {ownershipHold && (
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#FEE2E2] text-[#991B1B]" data-testid={`project-ownership-badge-${p.id}`}>
                          Ownership Change
                        </span>
                      )}
                      {p.is_completed && (
                        <span
                          className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${p.closure_type === "cancelled" ? "bg-[#FEE2E2] text-[#991B1B]" : "bg-[#D1FAE5] text-[#065F46]"}`}
                          data-testid={`project-completed-badge-${p.id}`}
                        >
                          {p.closure_type === "cancelled" ? "Cancelled" : "Completed"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#4B5563] break-words whitespace-normal">{p.location}</div>
                  </td>
                  <td className="px-4 py-3 font-bold" data-testid={`project-panel-cap-${p.id}`}>
                    {p.panel_capacity_kw ? `${p.panel_capacity_kw} kW` : <span className="text-[#9CA3AF] font-normal">—</span>}
                  </td>
                  <td className="px-4 py-3 font-bold" data-testid={`project-inv-cap-${p.id}`}>{p.capacity_kw} kW</td>
                  <td className="px-4 py-3 text-xs" data-testid={`project-panel-brand-${p.id}`}>
                    {p.panel_brand || <span className="text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs" data-testid={`project-inv-brand-${p.id}`}>
                    {p.inverter_brand || <span className="text-[#9CA3AF]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs">{p.project_manager || <span className="text-[#9CA3AF]">—</span>}</td>
                  <td className="px-4 py-3">{inr(p.contract_value_inr)}</td>
                  {isAdmin && (
                    <td className="px-4 py-3" data-testid={`project-profit-${p.id}`}>
                      {prof ? (
                        <div>
                          <div className="text-xs font-bold" style={{ color: marginColor }}>{inr(margin)}</div>
                          <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: marginColor }}>{marginPct}%</div>
                        </div>
                      ) : (
                        <span className="text-[#9CA3AF] text-xs">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-xs">{v2Current(p)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-24 bg-[#F3F4F6]">
                        <div className="h-full bg-[#002FA7]" style={{ width: `${v2Pct(p)}%` }} />
                      </div>
                      <span className="text-xs font-bold">{v2Pct(p)}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">{p.qr_code}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    {p.is_completed ? (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Reopen project "${p.customer_name}"?`)) return;
                          try {
                            await api.post(`/projects/${p.id}/reopen-completed`);
                            toast.success("Project reopened");
                            load();
                          } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                        }}
                        className="text-[#F59E0B] text-xs font-bold uppercase tracking-widest hover:underline"
                        data-testid={`project-reopen-${p.id}`}
                      >
                        Reopen
                      </button>
                    ) : (
                      <div className="flex flex-col gap-1 items-start">
                        <button
                          onClick={() => { setCompleteTarget(p); setCompleteNotes(""); setClosureType("completed"); }}
                          className="text-[#10B981] text-xs font-bold uppercase tracking-widest hover:underline"
                          data-testid={`project-complete-${p.id}`}
                        >
                          Complete Closure
                        </button>
                        <button
                          onClick={() => { setCompleteTarget(p); setCompleteNotes(""); setClosureType("cancelled"); }}
                          className="text-[#DC2626] text-[10px] font-bold uppercase tracking-widest hover:underline"
                          data-testid={`project-cancel-${p.id}`}
                        >
                          Cancel Project
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => setEditId(p.id)}
                      className="text-[#0a0a0a] text-xs font-bold uppercase tracking-widest hover:underline"
                      data-testid={`project-edit-${p.id}`}
                    >
                      Edit
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <RowDeleteButton
                      endpoint={`/projects/${p.id}`}
                      confirmMessage={`Delete project "${p.customer_name}" (${p.qr_code})?\n\nThis will CASCADE-DELETE all linked records:\n• Delivery checklists\n• Payments\n• Work schedules\n• Service tickets\n• Documents\n\nThis cannot be undone.`}
                      onDeleted={load}
                      testid={`project-delete-${p.id}`}
                      successMessage="Project deleted"
                      cascade
                    />
                  </td>
                </tr>
                );
              })}
              {projects.length === 0 && <tr><td colSpan={isAdmin ? 15 : 14} className="text-center text-sm text-[#9CA3AF] py-12">No projects match your search.</td></tr>}
            </tbody>
          </table>
        </div>

        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testidPrefix="projects-pagination"
        />
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditId(null)}>
        {editing && <ProjectDialog project={editing} onDone={() => { setEditId(null); load(); }} />}
      </Dialog>

      {/* Mark Project Completed / Cancelled — closure dialog with notes.
          Both types tick every stage sub-activity done + remove the project
          from dashboard delay alerts. Cancel requires notes so the reason
          for dropping the project is captured in the audit trail. */}
      <Dialog open={!!completeTarget} onOpenChange={(v) => !v && setCompleteTarget(null)}>
        <DialogContent className="rounded-none border-[#0a0a0a] max-w-md" data-testid="complete-project-dialog">
          <DialogHeader>
            <DialogTitle className="font-black text-2xl tracking-tight">
              {closureType === "cancelled" ? "Cancel Project?" : "Mark Project Completed?"}
            </DialogTitle>
          </DialogHeader>
          {completeTarget && (
            <div className="space-y-3 pt-2">
              <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-3 text-sm">
                <div className="font-semibold">{completeTarget.customer_name}</div>
                <div className="text-xs text-[#4B5563]">{completeTarget.location} · {completeTarget.qr_code}</div>
              </div>
              <div className="text-xs text-[#4B5563]">
                {closureType === "cancelled" ? (
                  <>Marks the project as <b>cancelled</b>. Every stage sub-activity is force-completed, and the project is removed from every dashboard alert. You can reopen it later if needed.</>
                ) : (
                  <>Marks the project as <b>handed over</b>. Every stage sub-activity is auto-ticked, the project moves to the <b>Completed</b> tab, and drops off dashboard alerts. Reopenable later.</>
                )}
              </div>
              <div>
                <Label className="label-eyebrow">
                  Notes {closureType === "cancelled" ? <span className="text-[#DC2626]">*</span> : "(optional)"}
                </Label>
                <Input
                  value={completeNotes}
                  onChange={(e) => setCompleteNotes(e.target.value)}
                  placeholder={closureType === "cancelled"
                    ? "e.g. Customer withdrew, feasibility rejected, dropped"
                    : "e.g. Handover documents shared, subsidy filed"}
                  className="rounded-none mt-2 h-10"
                  data-testid="complete-project-notes"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={() => setCompleteTarget(null)}
                  variant="outline"
                  className="flex-1 rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-10"
                  data-testid="complete-project-cancel"
                >Back</Button>
                <Button
                  onClick={async () => {
                    if (closureType === "cancelled" && !completeNotes.trim()) {
                      toast.error("Notes are required to cancel a project");
                      return;
                    }
                    try {
                      await api.post(`/projects/${completeTarget.id}/mark-completed`, {
                        notes: completeNotes || null,
                        closure_type: closureType,
                      });
                      toast.success(closureType === "cancelled" ? "Project cancelled" : "Project marked completed");
                      setCompleteTarget(null);
                      setCompleteNotes("");
                      setClosureType("completed");
                      load();
                    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                  }}
                  className={`flex-1 rounded-none uppercase tracking-widest text-xs font-bold h-10 ${closureType === "cancelled" ? "bg-[#DC2626] hover:bg-[#B91C1C]" : "bg-[#10B981] hover:bg-[#059669]"}`}
                  data-testid="complete-project-confirm"
                >
                  {closureType === "cancelled" ? "Confirm Cancellation" : "Confirm Completion"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProjectDialog({ project, onDone }) {
  const isEdit = !!project?.id;
  const [employees, setEmployees] = useState([]);
  const [proposalPrice, setProposalPrice] = useState(null);
  const [form, setForm] = useState({
    lead_id: project?.lead_id || "",
    customer_name: project?.customer_name || "",
    capacity_kw: project?.capacity_kw ?? "",
    panel_capacity_kw: project?.panel_capacity_kw ?? "",
    location: project?.location || "",
    map_link: project?.map_link || "",
    consumer_number: project?.consumer_number || "",
    has_subsidy: project?.has_subsidy == null ? "unset" : (project.has_subsidy ? "with" : "without"),
    contract_value_inr: project?.contract_value_inr ?? "",
    project_manager: project?.project_manager || "",
    inverter_serial: project?.inverter_serial || "",
    inverter_brand: project?.inverter_brand || "",
    panel_brand: project?.panel_brand || "",
    priority: project?.priority || "Low",
    ownership_change_needed: !!project?.ownership_change_needed,
    load_enhancement_needed: !!project?.load_enhancement_needed,
    feasibility_expiry_date: project?.feasibility_expiry_date || "",
    // Token payment captured at creation time — only applicable on new projects.
    token_amount_received: 0,
    token_method: "UPI",
    token_reference: "",
  });
  useEffect(() => {
    api.get("/users").then((r) => setEmployees((r.data || []).filter((u) => u.role !== "customer")));
  }, []);
  const set = (k) => (v) => setForm({ ...form, [k]: v.target ? v.target.value : v });

  const onLeadChange = async (v) => {
    let pm = form.project_manager;
    let price = null;
    if (v) {
      try {
        const r = await api.get(`/surveys?lead_id=${v}`);
        const s = r.data && r.data[0];
        if (s?.project_manager_name) pm = s.project_manager_name;
      } catch (_) { /* non-fatal */ }
      if (!isEdit) {
        try {
          const r = await api.get(`/leads/${v}/confirmed-proposal-price`);
          price = r.data?.final_price_inr ?? null;
        } catch (_) { /* non-fatal */ }
      }
    }
    setProposalPrice(price);
    setForm((prev) => ({ ...prev, lead_id: v, project_manager: pm,
      ...(price != null ? { contract_value_inr: price } : {}) }));
  };
  // Fired by the picker with the full lead option (pick, clear, initial
  // resolve). Skip autofill on initial resolve of an EDIT so existing values
  // are never silently overwritten.
  const onLeadSelect = (l, initial) => {
    if (!l || (initial && project)) return;
    setForm((prev) => ({
      ...prev,
      customer_name: l.name || prev.customer_name,
      location: l.address || prev.location,
      map_link: l.map_link || prev.map_link,
      consumer_number: l.consumer_number || prev.consumer_number,
      // Lead's capacity always wins when a lead is picked (per user requirement);
      // empty string when no lead and field is untouched.
      capacity_kw: l.capacity_kw ?? prev.capacity_kw,
      panel_capacity_kw: l.panel_capacity_kw ?? prev.panel_capacity_kw,
    }));
  };

  const submit = async () => {
    if (!form.customer_name) { toast.error("Enter customer name"); return; }
    if (form.capacity_kw === "" || form.capacity_kw === null) { toast.error("Enter inverter capacity (kW)"); return; }
    if (form.contract_value_inr === "" || form.contract_value_inr === null) { toast.error("Enter contract value"); return; }
    const payload = {
      ...form,
      capacity_kw: parseFloat(form.capacity_kw),
      panel_capacity_kw: form.panel_capacity_kw === "" || form.panel_capacity_kw == null ? null : parseFloat(form.panel_capacity_kw),
      contract_value_inr: parseFloat(form.contract_value_inr),
      lead_id: form.lead_id || null,
      consumer_number: (form.consumer_number || "").trim() || null,
      has_subsidy: form.has_subsidy === "with" ? true : form.has_subsidy === "without" ? false : null,
      feasibility_expiry_date: form.feasibility_expiry_date || null,
      // Token payment only applies on create.
      token_amount_received: isEdit ? 0 : (Number(form.token_amount_received) || 0),
      token_method: (!isEdit && Number(form.token_amount_received) > 0) ? form.token_method : null,
      token_reference: (!isEdit && Number(form.token_amount_received) > 0) ? (form.token_reference || null) : null,
    };
    try {
      if (isEdit) {
        await api.patch(`/projects/${project.id}`, payload);
        toast.success("Project updated");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created");
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-black text-2xl tracking-tight">
          {isEdit ? `Edit Project · ${project.qr_code}` : "New Project"}
        </DialogTitle>
      </DialogHeader>
      <div className="space-y-3 pt-2">
        <div>
          <Label className="label-eyebrow">From Lead (optional)</Label>
          <SearchablePicker
            items={[]}
            value={form.lead_id}
            onChange={onLeadChange}
            optionsEndpoint="/leads/options"
            onSelectItem={onLeadSelect}
            renderRow={renderLeadRow}
            selectedLabel={(l) => l.name || "Selected lead"}
            placeholder="Search by name, phone or district…"
            disabled={isEdit}
            testid="project-lead-select"
            emptyHint="No leads match your search."
          />
          {isEdit && <div className="text-[10px] text-[#4B5563] mt-1">Lead link cannot be changed after creation.</div>}
        </div>
        <Field label="Customer Name" value={form.customer_name} onChange={set("customer_name")} testid="project-customer" />
        <Field label="Location" value={form.location} onChange={set("location")} testid="project-location" />
        <Field
          label="Google Map Link (optional)"
          value={form.map_link}
          onChange={set("map_link")}
          testid="project-map-link"
        />
        <div className="grid grid-cols-2 gap-2">
          <Field
            label="Consumer Number (KSEB)"
            value={form.consumer_number}
            onChange={set("consumer_number")}
            testid="project-consumer-number"
          />
          <div>
            <Label className="label-eyebrow">Subsidy</Label>
            <Select value={form.has_subsidy} onValueChange={set("has_subsidy")}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="project-subsidy-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="unset">— Not set —</SelectItem>
                <SelectItem value="with">With Subsidy</SelectItem>
                <SelectItem value="without">Without Subsidy</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Inverter Capacity (kW)" type="number" value={form.capacity_kw} onChange={set("capacity_kw")} testid="project-capacity" />
          <Field label="Panel Capacity (kW)" type="number" value={form.panel_capacity_kw} onChange={set("panel_capacity_kw")} testid="project-panel-capacity" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Contract Value (₹)" type="number" value={form.contract_value_inr} onChange={set("contract_value_inr")} testid="project-value" />
          <div />
        </div>
        {proposalPrice != null && (
          <div className="text-[10px] text-[#4B5563] -mt-1" data-testid="project-value-from-proposal">
            Taken from confirmed proposal final price: <b>{inr(proposalPrice)}</b>
            {Number(form.contract_value_inr) !== Number(proposalPrice) && <span className="text-[#F59E0B] font-bold"> · edited</span>}
          </div>
        )}
        <div>
          <Label className="label-eyebrow">Project Manager</Label>
          <div className="mt-2">
            <EmployeePicker
              employees={employees}
              value={(employees.find((u) => u.name === form.project_manager) || {}).id}
              onChange={(id, u) => set("project_manager")(u ? u.name : "")}
              placeholder="Select Project Manager…"
              testid="project-pm-select"
            />
          </div>
          {form.project_manager && !employees.some((u) => u.name === form.project_manager) && (
            <div className="text-[10px] text-[#4B5563] mt-1">Currently: <b>{form.project_manager}</b> (not in employee list)</div>
          )}
        </div>
        <Field label="Panel Brand" value={form.panel_brand} onChange={set("panel_brand")} testid="project-panel-brand" />
        <Field label="Inverter Brand" value={form.inverter_brand} onChange={set("inverter_brand")} testid="project-inverter-brand" />
        <div>
          <Label className="label-eyebrow">Priority</Label>
          <Select value={form.priority || "Low"} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="rounded-none mt-2 h-10" data-testid="project-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map((p) => (
                <SelectItem key={p} value={p}>
                  <span className="flex items-center gap-2">
                    <PriorityBadge priority={p} size="xs" />
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[10px] text-[#9CA3AF] mt-1">
            Colour-coded badges show up in the list and the Work Scheduler.
          </div>
        </div>
        <div>
          <Label className="label-eyebrow">Feasibility Expiry Date (optional)</Label>
          <Input
            type="date"
            value={form.feasibility_expiry_date}
            onChange={set("feasibility_expiry_date")}
            className="rounded-none mt-2 h-10"
            data-testid="project-feasibility-expiry"
          />
          <div className="text-[10px] text-[#9CA3AF] mt-1">
            When the KSEB feasibility approval expires. Projects expiring within 15 days show up on the dashboard alert.
          </div>
        </div>
        {!isEdit && (
          <div className="border border-[#002FA7] bg-[#EFF6FF] p-3 space-y-2" data-testid="project-token-section">
            <div className="label-eyebrow !text-[10px] text-[#002FA7]">Token Payment (optional)</div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="label-eyebrow !text-[10px]">Amount (₹)</Label>
                <Input
                  type="number"
                  step="any"
                  value={form.token_amount_received}
                  onChange={set("token_amount_received")}
                  className="rounded-none mt-1 h-10"
                  placeholder="0"
                  data-testid="project-token-amount"
                />
              </div>
              <div>
                <Label className="label-eyebrow !text-[10px]">Method</Label>
                <Select value={form.token_method} onValueChange={set("token_method")}>
                  <SelectTrigger className="rounded-none mt-1 h-10" data-testid="project-token-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{["UPI", "Cash", "Cheque", "Bank Transfer", "Card", "NEFT/IMPS"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow !text-[10px]">Reference / UTR</Label>
                <Input
                  value={form.token_reference}
                  onChange={set("token_reference")}
                  className="rounded-none mt-1 h-10"
                  placeholder="UPI txn id, cheque no…"
                  data-testid="project-token-reference"
                />
              </div>
            </div>
            <div className="text-[10px] text-[#4B5563]">
              If entered, this is logged in <b>Finance → Pending for Confirmation</b> as a Token Payment and waits for admin/finance verification before counting toward Collections Received.
            </div>
          </div>
        )}
        <div className="border border-[#E5E7EB] p-3 space-y-2">
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.load_enhancement_needed}
              onChange={(e) => setForm({ ...form, load_enhancement_needed: e.target.checked })}
              className="w-4 h-4 accent-[#DC2626]"
              data-testid="project-load-enh-checkbox"
            />
            <span>Load Enhancement Needed</span>
          </label>
          <div className="text-[10px] text-[#9CA3AF] pl-6">
            When checked, this project is highlighted in red on the Projects list to flag load enhancement follow-up.
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.ownership_change_needed}
              onChange={(e) => setForm({ ...form, ownership_change_needed: e.target.checked })}
              className="w-4 h-4 accent-[#991B1B]"
              data-testid="project-ownership-checkbox"
            />
            <span>Ownership Change Needed</span>
          </label>
        </div>
        <Field label="Inverter Serial Number" value={form.inverter_serial} onChange={set("inverter_serial")} testid="project-inverter-serial" />
        <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="project-submit">
          {isEdit ? "Save Changes" : "Create Project"}
        </Button>
      </div>
    </DialogContent>
  );
}
function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input type={type} value={value} onChange={onChange} className="rounded-none mt-2 h-10" data-testid={testid} />
    </div>
  );
}
